import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { StoreFeatures } from '@state/store.utils';
import { DataOutsideEUResponse, DefaultData, UsageChart } from '@core/models';
import { GeneralActions } from '@state/general/general.actions';
import {
  BillingResponse,
  CommunicationResponse,
  CustomerInvoiceResponse,
  NetworkSettingsResponse,
  NewSubscriptionsResponse,
  PrivacyResponse,
} from '@core/contracts/data-contracts';
import { UserActions } from '@state';

export type GeneralState = {
  usageChart: UsageChart;
  isLoading: boolean;

  allInvoices: CustomerInvoiceResponse[];
  paidInvoices: CustomerInvoiceResponse[];
  outstandingInvoices: CustomerInvoiceResponse[];

  adPreferences: CommunicationResponse | null;
  privacySettings: PrivacyResponse | null;

  simSettings: {
    pukCode: string | null;
    simCardType: number | null;
    esimProfile: {
      convertedProfile: string | null;
      activationCode: string | null;
      smdpAddress: string | null;
    } | null;
    isSimReplaceSuccess: boolean;
    isBlocked: boolean;
    isActivated: boolean;
    isActivationPending: boolean;
  };

  networkSettings: NetworkSettingsResponse | null;
  availableSubscriptions: NewSubscriptionsResponse | null;
  dataOutsideEUTariffs: DataOutsideEUResponse;
  isDataOutsideEUTariffsLoading: boolean;

  bankAccount: {
    info: BillingResponse | null;
    isChanging: boolean;
    changeUrl: string | null;
  };

  storagePrice: number | null;
};

export const initialGeneralState: GeneralState = {
  usageChart: DefaultData.overviewUsageChart,
  isLoading: false,

  allInvoices: [],
  paidInvoices: [],
  outstandingInvoices: [],

  adPreferences: null,
  privacySettings: null,

  simSettings: {
    pukCode: null,
    isSimReplaceSuccess: false,
    simCardType: null,
    esimProfile: null,
    isBlocked: false,

    isActivated: false,
    isActivationPending: false,
  },

  networkSettings: null,
  availableSubscriptions: null,
  dataOutsideEUTariffs: {
    available: [],
    bought: [],
  },
  isDataOutsideEUTariffsLoading: true,

  bankAccount: {
    info: null,
    isChanging: false,
    changeUrl: null,
  },

  storagePrice: null,
};

export const generalReducer = createReducer(
  initialGeneralState,
  on(
    GeneralActions.overviewGetUsageDetails,
    (state): GeneralState => ({
      ...state,
      isLoading: true,
    })
  ),
  on(
    GeneralActions.overviewGetUsageDetailsFailure,
    (state): GeneralState => ({
      ...state,
      isLoading: false,
    })
  ),
  on(
    GeneralActions.overviewGetUsageDetailsSuccess,
    (state, { usageChart }): GeneralState => ({
      ...state,
      usageChart,
      isLoading: false,
    })
  ),
  on(
    GeneralActions.overviewClearUsageDetails,
    (state): GeneralState => ({
      ...state,
      usageChart: DefaultData.overviewUsageChart,
      isLoading: false,
    })
  ),
  on(
    GeneralActions.getAllInvoicesSuccess,
    (state, { allInvoices }): GeneralState => ({
      ...state,
      allInvoices,
    })
  ),
  on(
    GeneralActions.getPaidInvoicesSuccess,
    (state, { paidInvoices }): GeneralState => ({
      ...state,
      paidInvoices,
    })
  ),
  on(
    GeneralActions.getOutstandingInvoicesSuccess,
    (state, { outstandingInvoices }): GeneralState => ({
      ...state,
      outstandingInvoices,
    })
  ),
  on(
    GeneralActions.getPrivacySettingsSuccess,
    (state, { privacySettings }): GeneralState => ({
      ...state,
      privacySettings,
    })
  ),
  on(
    GeneralActions.getAdPreferencesSuccess,
    (state, { adPreferences }): GeneralState => ({
      ...state,
      adPreferences,
    })
  ),
  on(
    GeneralActions.getPukCodeSuccess,
    (state, { pukCode }): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        pukCode,
      },
    })
  ),
  on(
    GeneralActions.clearPukCode,
    (state): GeneralState => ({
      ...state,
      simSettings: initialGeneralState.simSettings,
    })
  ),
  on(
    GeneralActions.replaceSimCardSuccess,
    (state): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        isSimReplaceSuccess: true,
      },
    })
  ),
  on(
    GeneralActions.getSimCardTypeSuccess,
    (state, { simCardType }): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        simCardType,
      },
    })
  ),
  on(
    GeneralActions.blockSimCardSuccess,
    (state): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        isBlocked: true,
      },
    })
  ),
  on(
    GeneralActions.unblockSimCardSuccess,
    (state): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        isBlocked: false,
      },
    })
  ),
  on(
    GeneralActions.activateSimCardSuccess,
    (state): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        isActivationPending: false,
      },
    })
  ),
  on(
    GeneralActions.getEsimDataSuccess,
    (state, { convertedProfile, activationCode, smdpAddress }): GeneralState => ({
      ...state,
      simSettings: {
        ...state.simSettings,
        esimProfile: {
          convertedProfile,
          activationCode,
          smdpAddress,
        },
      },
    })
  ),
  on(
    GeneralActions.getNetworkSettingsSuccess,
    (state, { networkSettings }): GeneralState => ({
      ...state,
      networkSettings,
    })
  ),
  on(
    GeneralActions.updateNetworkSettingsSuccess,
    (state, { payload }): GeneralState => ({
      ...state,
      networkSettings: {
        ...state.networkSettings,
        ...payload,
      },
    })
  ),
  on(
    GeneralActions.updateNetworkSettingsFailure,
    (state): GeneralState => ({
      ...state,
      networkSettings: {
        ...state.networkSettings,
      },
    })
  ),
  on(
    GeneralActions.updateAdPreferencesSuccess,
    (state, { payload }): GeneralState => ({
      ...state,
      adPreferences: {
        ...state.adPreferences,
        ...payload,
      },
    })
  ),
  on(
    GeneralActions.updateAdPreferencesFailure,
    (state): GeneralState => ({
      ...state,
      adPreferences: {
        ...state.adPreferences,
      },
    })
  ),
  on(
    GeneralActions.getBankAccountInfoSuccess,
    (state, { bankAccountInfo }): GeneralState => ({
      ...state,
      bankAccount: {
        ...state.bankAccount,
        info: bankAccountInfo,
      },
    })
  ),
  on(
    GeneralActions.requestChangeBankAccountSuccess,
    (state, { url }): GeneralState => ({
      ...state,
      bankAccount: {
        ...state.bankAccount,
        isChanging: true,
        changeUrl: url,
      },
    })
  ),
  on(
    GeneralActions.getAvailableSubscriptionsSuccess,
    (state, { availableSubscriptions }): GeneralState => ({
      ...state,
      availableSubscriptions,
    })
  ),
  on(
    GeneralActions.getDataOutsideEUSuccess,
    (state, { tariffs }): GeneralState => ({
      ...state,
      dataOutsideEUTariffs: tariffs,
      isDataOutsideEUTariffsLoading: false,
    })
  ),
  on(UserActions.signOut, (): GeneralState => initialGeneralState),

  on(
    GeneralActions.getStoragePriceSuccess,
    (state, { price }): GeneralState => ({
      ...state,
      storagePrice: price,
    })
  )
);

export const generalFeature = createFeature({
  name: StoreFeatures.General,
  reducer: generalReducer,
  extraSelectors: ({ selectSimSettings, selectBankAccount, selectUsageChart }) => ({
    selectIsSimBlocked: createSelector(selectSimSettings, (query) => query.isBlocked),
    selectIsSimActivationPending: createSelector(
      selectSimSettings,
      (query) => query.isActivationPending
    ),
    selectBankAccountInfo: createSelector(selectBankAccount, (query) => query.info),
    selectIsBankAccountChanging: createSelector(selectBankAccount, (query) => query.isChanging),
    selectSimCardType: createSelector(selectSimSettings, (query) => query.simCardType),
    selectEsimProfile: createSelector(selectSimSettings, (query) => query.esimProfile),
    selectIsDataOnly: createSelector(selectUsageChart, (query) => query.isDataOnly),
  }),
});
